/**
 * GitLabGroupPicker - custom field do Scaffolder.
 *
 * Lista grupos e subgrupos do git.suporte.ibict.br num dropdown
 * pesquisavel, no formato "GRUPO/SUBGRUPO". O usuario nao precisa
 * digitar paths a mao nem saber a hierarquia.
 *
 * Consulta o endpoint backend /api/ibict/gitlab/groups. O backend usa o
 * usuario logado no Backstage/OIDC para localizar o usuario GitLab
 * correspondente e retorna apenas grupos/subgrupos ligados as memberships
 * dele.
 */

import { useEffect, useState } from 'react';
import {
  CircularProgress,
  FormControl,
  TextField,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useApi, fetchApiRef } from '@backstage/core-plugin-api';
import {
  createFormField,
  FormFieldBlueprint,
} from '@backstage/plugin-scaffolder-react/alpha';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

interface GitlabGroup {
  id: number;
  full_path: string;
  name: string;
  access_level: number;
}

interface GitlabGroupsResponse {
  groups: GitlabGroup[];
}

const GROUPS_PATH = '/api/ibict/gitlab/groups';

const GitLabGroupPicker = (
  props: FieldExtensionComponentProps<string>,
) => {
  const { onChange, formData, rawErrors, required, schema } = props;
  const fetchApi = useApi(fetchApiRef);

  const [groups, setGroups] = useState<GitlabGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetchApi.fetch(GROUPS_PATH);
        if (!res.ok) {
          throw new Error(`HTTP ${res.status} ${res.statusText}`);
        }
        const payload = (await res.json()) as GitlabGroupsResponse;
        if (!cancelled) {
          const sorted = [...payload.groups].sort((a, b) =>
            a.full_path.localeCompare(b.full_path),
          );
          setGroups(sorted);
        }
      } catch (e: any) {
        if (!cancelled) setError(e.message ?? String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [fetchApi]);

  return (
    <FormControl
      margin="normal"
      required={required}
      error={Boolean(rawErrors?.length) || Boolean(error)}
      fullWidth
    >
      <Autocomplete<string, false, false, false>
        options={groups.map(g => g.full_path)}
        value={formData ?? null}
        onChange={(_event, value) => onChange((value as string) ?? '')}
        loading={loading}
        autoSelect
        renderInput={(params: any) => (
          <TextField
            {...params}
            label={(schema.title as string) ?? 'Grupo'}
            helperText={
              error
                ? `Falha ao carregar grupos: ${error}`
                : ((schema.description as string) ??
                  'Selecione o grupo (ou subgrupo) onde o repositorio sera criado')
            }
            variant="outlined"
            required={required}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </FormControl>
  );
};

export const GitLabGroupPickerField = createFormField({
  name: 'GitLabGroupPicker',
  component: GitLabGroupPicker,
});

export const gitLabGroupPickerField = FormFieldBlueprint.make({
  name: 'gitlab-group-picker',
  params: {
    field: async () => GitLabGroupPickerField,
  },
});
